/**
 *  Singleton performing simple storage methods
 *
 *
 *  (?) Unclear needs / adopt KISS principle:
 *
 *    a) Which data will be stored? Can we dissociate some
 *       business → "authentication cache", "categorisation cache", etc?
 *
 *          > only data regarding authentication, but we can prefix set items
 *            to properly scoped data
 *
 *          > (!) if an evolution is made towards another storage type,
 *                getting non existent stores/key will might throw Error
 *                (leading to a possible refactoring)
 *
 *
 *    b) How to set cache persistence? Can we separate the need of
 *       "browser tab/page" cache lifetime, "browser session" cache
 *       lifetime, "defined expiration" cache lifetime?
 *
 *          > we'll use only 2 two cache lifetime: "browser tab/page" → no
 *            storage needed ; "defined expiration" → localStorage is the most
 *            suitable and less sophisticated
 *
 *          > (!) an extreme simplification for this point, we omit the
 *                whole creation/connection to storage here, this will
 *                raise two issues later:
 *
 *                1. adding the first step of creation/connection within
 *                   the application
 *
 *                2. managing storage releases (w/ migration callback),
 *                   implying that for now NO MIGRATION will be handled,
 *                   thus to change cache we will need an API version
 *                   bump release
 *
 *
 *      c) Is stored data will be lectured frequently? Can we analyze the
 *         need for a concurrency management?

 *
 *          > we can assume no, but we'll expose simple async CRUD methods,
 *            and store formatted, in case of an evolution towards a more
 *            consistent storage (such as IndexedDB)
 */
import { each, filter, fromJSON, join, keys, merge, pipe_, startsWith, typeOf} from '~/utils/lib/core/prelude'
import Console from '~/utils/lib/core/console'

const DATABASE_NAME = 'cloud-ghs'
const KEY_SEPARATOR = '::'
const CONFIG = {
  verbose: false
}
/**
 * Check if storage is available
 * @return {boolean}
 */
const console = new Console('plugin:storage')
export const ERROR_MESSAGES = {
  STORAGE_NOT_AVAILABLE: 'localStorage is not available',
}
export function isStorageAvailable() {
  try {
    return window.localStorage && typeOf(window.localStorage) === 'storage'
  }
  catch { return false }
}
function getStorageHandler() {
  if (isStorageAvailable()) {
    return window.localStorage
  }
  else {
    throw new Error(ERROR_MESSAGES.STORAGE_NOT_AVAILABLE)
  }
}
const storage = {
  // Reference to storage handler in use
  // @why getter defers check to first use, letting bootloader catch errors via `error.vue`
  get handler() { return getStorageHandler() },
  /**
   *  @function set
   *  @param {string} store
   *  @param {string} key
   *  @param {*} value
   *  @param {Partial<typeof CONFIG>} [options={}] merged with `CONFIG`
   *  @return {Promise<*>} set value
   */
  set(store, key, value, options = {}) {
    const params = merge(CONFIG, options)
    const keyName = join(KEY_SEPARATOR, [
      DATABASE_NAME,
      store,
      key
    ])
    const keyValue = JSON.stringify(value)

    this.handler.setItem(keyName, keyValue)

    if (params.verbose === true)
      console.log('set', keyName, keyValue)

    return this.get(store, key)
  },
  /**
   *  @function get
   *  @param {string} store
   *  @param {string} key
   *  @return {Promise<*>}
   */
  get(store, key) {
    const keyName = join(KEY_SEPARATOR, [
      DATABASE_NAME,
      store,
      key
    ])
    const keyValue = fromJSON(this.handler.getItem(keyName))

    return Promise.resolve(keyValue)
  },
  /**
   * @function delete
   * @param {string} store
   * @param {string} key
   * @return {Promise<void>}
   */
  delete(store, key) {
    const keyName = join(KEY_SEPARATOR, [
      DATABASE_NAME,
      store,
      key
    ])

    this.handler.removeItem(keyName)

    return Promise.resolve()
  },
  /**
   * @function destroyStore
   * @desc destroy *one* given store
   * @promise-never-rejects
   * @param {string} store
   * @return {Promise<void>}
   */
  destroyStore(store) {
    const filter_ = join('', [
      DATABASE_NAME,
      KEY_SEPARATOR,
      store,
      KEY_SEPARATOR
    ])

    pipe_(
      keys( this.handler ),
      filter(
        startsWith(filter_)
      ),
      each.iterable(
        // @XXX Ramda pipe + each/map/reduce function sometimes throws error
        //    when using currying
        //      ↳ empirical occurence: withold currying usage
        item => this.handler.removeItem(item)
      ),
    )

    console.log('store destroyed', store)

    return Promise.resolve()
  },
  /**
   * @function destroy
   * @desc destroy *every* stores related to our database
   * @promise-never-rejects
   * @return {Promise<void>}
   */
    destroy() {
      const filter_ = `${DATABASE_NAME}${KEY_SEPARATOR}`

      pipe_(
        keys( this.handler ),
        filter(
          startsWith(filter_)
        ),
        each.iterable(
          // @XXX Ramda pipe + each/map/reduce function sometimes throws error
          //    when using currying
          //      ↳ empirical occurence: withold currying usage
          item => this.handler.removeItem(item)
        ),
      )

      console.log('destroyed')

      return Promise.resolve()
    },
  /**
   * @function unsafeDestroy
   * @desc destroy EVERYTHING
   * @promise-never-rejects
   * @return {Promise<void>}
   */
  unsafeDestroy() {
    this.handler.clear()

    console.log('UNSAFE destroyed')

    return Promise.resolve()
  },
}

export default storage
